import { useMemo, useState } from "react"

import { Empty, Flex, Spin, Tooltip, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"

import { DashboardFiltersBlock } from "../../../Components/Filters/DashboardFiltersBlock/DashboardFiltersBlock"
import Private from "../../../Components/Private"
import SelectComponent from "../../../Components/Select"
import TableComponent from "../../../Components/Table"
import Icon from "../../../Icon"
import Images from "../../../Images"
import HealthMobilePagination from "../components/HealthMobilePagination"
import HealthSubpageHeader from "../components/HealthSubpageHeader"
import HealthSubpageLayout from "../components/HealthSubpageLayout"
import {
  formatAmount,
  formatCount,
  getColumns
} from "../components/Table/chargebackOlympicsColumns"
import { useHealthDataFetch, useHealthFilters, useHealthSubpage, useTableSettings } from "../hooks"
import { healthSetProp } from "../redux/health.slice"
import { getDataOlympics } from "../redux/health.thunks"
import { useBusinessAccountId } from "../VAMP/hooks/useBusinessAccountId"

import "../components/ChargebackOlympics/styles.less"

const { Text, Link } = Typography

const pageSize = 50

const ChargebackOlympicsPage = ({ filters, dataOlympicsFetching, dataOlympics, ...props }) => {
  const { t } = useTranslation()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const businessId = useBusinessAccountId()
  const { count, results } = dataOlympics
  const [mobileColumn, setMobileColumn] = useState("chargebacks")
  const { dataSource } = useSelector((state) => state.health)

  // Custom hooks for common logic
  const { pageNumber, ordering, handleChangeTable, setPageNumber } = useHealthSubpage(pageSize)
  const { handleChangeFilter } = useHealthFilters(props.changeFilters)

  // Data fetching
  useHealthDataFetch({
    filters,
    businessId,
    pageNumber,
    pageSize,
    ordering,
    fetchAction: props.getDataOlympics,
    dataSource
  })

  // Memoized columns
  const columnsByCardType = useMemo(() => getColumns({ t, isFullTable: true, withSort: true }), [t])

  // Custom scroll settings for Olympics table
  const customScroll = useMemo(
    () => ({
      x: count ? "max-content" : "auto",
      y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px))",
      scrollToFirstRowOnChange: true
    }),
    [count]
  )

  const tableSettings = useTableSettings(count, pageNumber, pageSize, customScroll)

  const mobileColumnOptions = useMemo(
    () => [
      { label: t("Chargebacks"), value: "chargebacks" },
      { label: t("Chargebacks volume"), value: "chargebacks_amount" },
      { label: t("Alerts"), value: "alerts" },
      { label: t("Alerts volume"), value: "alerts_amount" },
      { label: t("Email"), value: "email" },
      { label: t("Country"), value: "country" }
    ],
    [t]
  )

  const selectedMobileColumnLabel =
    mobileColumnOptions.find((o) => o.value === mobileColumn)?.label || t("Column")

  const renderCustomerName = (record) => {
    const name = record?.customer_name
    if (!name || name === "N/A") {
      return (
        <Tooltip title="Customer information is missing, or the account no longer exists">
          <Text>N/A</Text>
        </Tooltip>
      )
    }
    const customerLink = record?.stripe_link || record?.shopify_link
    if (customerLink) {
      return (
        <Link href={customerLink} target="_blank">
          <Private>{name}</Private>
        </Link>
      )
    }
    return (
      <Text strong>
        <Private>{name}</Private>
      </Text>
    )
  }

  const renderMobileValueCell = (record) => {
    const renderCount = (value) => (value === "-" ? "-" : formatCount(value))
    switch (mobileColumn) {
      case "chargebacks":
        return (
          <Flex gap={2} vertical align="flex-end">
            <Text strong>{renderCount(record?.chargebacks_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(record?.chargebacks_amount)}</Private>
            </Text>
          </Flex>
        )
      case "chargebacks_amount":
        return (
          <Text strong>
            <Private>{formatAmount(record?.chargebacks_amount)}</Private>
          </Text>
        )
      case "alerts":
        return (
          <Flex gap={2} vertical align="flex-end">
            <Text strong>{renderCount(record?.alerts_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(record?.alerts_amount)}</Private>
            </Text>
          </Flex>
        )
      case "alerts_amount":
        return (
          <Text strong>
            <Private>{formatAmount(record?.alerts_amount)}</Private>
          </Text>
        )
      case "email":
        return <Text strong>{record?.email ? <Private>{record.email}</Private> : "-"}</Text>
      case "country":
        return <Text strong>{record?.country || "-"}</Text>
      default:
        return null
    }
  }

  return (
    <HealthSubpageLayout backText={t("Back to Health")} minHeight="auto" isMobile={isMobile}>
      {isMobile ? (
        <Flex vertical gap={12} className="chargeback-olympics-subpage-mobile__header-block">
          <HealthSubpageHeader title={t("Chargeback Olympics")} />
          <DashboardFiltersBlock
            handleChangeFilter={handleChangeFilter}
            filters={filters}
            onlyPeriod={true}
          />
          <SelectComponent
            size="large"
            value={mobileColumn}
            onChange={setMobileColumn}
            options={mobileColumnOptions}
            style={{ width: "100%" }}
          />
        </Flex>
      ) : (
        <HealthSubpageHeader title={t("Chargeback Olympics")}>
          <DashboardFiltersBlock
            handleChangeFilter={handleChangeFilter}
            filters={filters}
            onlyPeriod={true}
          />
        </HealthSubpageHeader>
      )}

      {isMobile ? (
        <div>
          <Flex vertical gap={12} className="chargeback-olympics-subpage-mobile">
            <div className="chargeback-olympics-subpage-mobile__header">
              <Text type="secondary">{t("Customer name")}</Text>
              <Text type="secondary" className="chargeback-olympics-subpage-mobile__header-right">
                {selectedMobileColumnLabel}
              </Text>
            </div>

            {dataOlympicsFetching ? (
              <Flex justify="center" style={{ padding: "24px 0" }}>
                <Spin spinning={true} />
              </Flex>
            ) : results?.length ? (
              results.map((record, index) => (
                <div
                  key={record.sequence_number}
                  className={`chargeback-olympics-subpage-mobile__row${
                    index === results.length - 1
                      ? " chargeback-olympics-subpage-mobile__row--last"
                      : ""
                  }`}
                >
                  <Flex gap={8} align="center" className="chargeback-olympics-subpage-mobile__left">
                    {record?.sequence_number <= 3 ? (
                      <Icon type={`medal${record.sequence_number}`} />
                    ) : (
                      <Text className="chargeback-olympics-subpage-mobile__rank">
                        {record.sequence_number}
                      </Text>
                    )}
                    {renderCustomerName(record)}
                  </Flex>
                  <div className="chargeback-olympics-subpage-mobile__right">
                    {renderMobileValueCell(record)}
                  </div>
                </div>
              ))
            ) : (
              <Flex justify="center" style={{ padding: "8px 0 16px" }}>
                <Empty
                  image={Images.no_data}
                  imageStyle={{ width: 64, height: 40 }}
                  description={t("No Data")}
                />
              </Flex>
            )}
          </Flex>

          <HealthMobilePagination
            current={pageNumber}
            pageSize={pageSize}
            total={count || 0}
            onChange={setPageNumber}
          />
        </div>
      ) : (
        <TableComponent
          loading={dataOlympicsFetching}
          dataSource={results}
          columns={columnsByCardType}
          scroll={tableSettings.scroll}
          onChange={handleChangeTable}
          pagination={tableSettings.pagination}
          rowKey="label"
          className="chargeback-olympics-table"
        />
      )}
    </HealthSubpageLayout>
  )
}

const mapStateToProps = (state) => ({
  filters: state.health.filters,
  dataOlympicsFetching: state.health.dataOlympicsFetching,
  dataOlympics: state.health.dataOlympics
})

const mapDispatchToProps = (dispatch) => ({
  getDataOlympics: (params) => dispatch(getDataOlympics(params)),
  changeFilters: (filters) => dispatch(healthSetProp({ key: "filters", value: filters }))
})

export default connect(mapStateToProps, mapDispatchToProps)(ChargebackOlympicsPage)
